<template>
  <section class="credential">
    <div class="credential-photo">
      <img v-if="avatarUrl" :src="avatarUrl" alt="Foto de perfil" class="credential-img" />
      <span v-else-if="initials" class="credential-initials">{{ initials }}</span>
      <PortalIcon v-else name="user" :size="32" />
    </div>

    <h2 class="credential-name">{{ name || "—" }}</h2>
    <div class="credential-role">{{ idLine }}</div>

    <dl class="credential-facts">
      <!-- Only what has been given: each shows up as soon as it is (see CandidatePortal's live profile). -->
      <div v-if="bloodType" class="fact">
        <dt>Tipo de sangre</dt>
        <dd>{{ bloodType }}</dd>
      </div>
      <div v-if="phone" class="fact fact-mono">
        <dt>Celular</dt>
        <dd>{{ phone }}</dd>
      </div>
      <div class="fact fact-status" :class="submitted ? 'is-sent' : 'is-progress'">
        <dt>Solicitud</dt>
        <dd>{{ submitted ? "Enviada" : "En progreso" }}</dd>
      </div>
      <div v-if="lastUpdate" class="fact">
        <dt>Última actualización</dt>
        <dd>{{ lastUpdate }}</dd>
      </div>
    </dl>
  </section>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import { applicationStep, formatDate, fullName, initials } from "./candidateSummary";

// The identification's label, by its type (values as in constants/id-types.js).
const ID_LABELS = { 1: "Cédula física", 2: "DIMEX", 3: "Pasaporte" };

export default {
  name: "PortalCredential",
  components: { PortalIcon },
  props: {
    candidate: { type: Object, required: true },
    // Signed link to the profile photo, if there is one.
    avatarUrl: { type: String, default: null },
    // The registry's name for the cédula, used until the candidate has one of their own (see CandidatePortal).
    registryName: { type: Object, default: null },
  },
  computed: {
    // Whose name the card shows: the saved one, otherwise the registry's.
    nameSource() {
      return this.candidate.first_name || !this.registryName ? this.candidate : this.registryName;
    },
    name() {
      return fullName(this.nameSource);
    },
    initials() {
      return initials(this.nameSource);
    },
    idLabel() {
      return ID_LABELS[this.candidate.identification_type_id] || "Identificación";
    },
    // Under the name: "Cédula física: 305040650" (just "Candidato" while there is no identification yet).
    idLine() {
      return this.candidate.gov_id ? `${this.idLabel}: ${this.candidate.gov_id}` : "Candidato";
    },
    bloodType() {
      const value = this.candidate.blood_type_id;
      return value === "ND" ? "No definido" : value;
    },
    phone() {
      return this.candidate.whatsapp_phone || this.candidate.cell_phone;
    },
    submitted() {
      return applicationStep(this.candidate) >= 4;
    },
    // The form stamps sworn_statement_date with the day of every save, so it doubles as the last change.
    lastUpdate() {
      return formatDate(this.candidate.sworn_statement_date);
    },
  },
};
</script>

<style scoped>
.credential {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 18px 18px;
  overflow: hidden;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.06), 0 12px 32px -8px rgba(10, 16, 32, 0.18);
  text-align: center;
}
/* The gold band across the top; the photo overlaps its lower edge. */
.credential::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 60px;
  background: linear-gradient(135deg, #e6b65c, #b7832a);
}
.credential-photo {
  position: relative;
  z-index: 1;
  width: 76px;
  height: 76px;
  /* Centered on the gold band's lower edge: band height (60px) minus half the photo (38px). */
  margin-top: 22px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #fff;
  border-radius: 18px;
  background: #1a2338;
  color: #8c96ad;
  box-shadow: 0 8px 18px -6px rgba(10, 16, 32, 0.35);
}
.credential-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.credential-initials {
  font: 700 26px "Geist", system-ui, sans-serif;
  color: #fff;
  letter-spacing: 0.02em;
}
.credential-name {
  margin: 10px 0 0;
  font: 600 19px/1.25 "Geist", system-ui, sans-serif;
  letter-spacing: -0.02em;
  color: #0e1424;
  overflow-wrap: anywhere;
  text-wrap: balance;
}
.credential-role {
  margin-top: 2px;
  font: 400 13px "Geist", system-ui, sans-serif;
  color: #5d6578;
}
.credential-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  margin: 14px 0 0;
  overflow: hidden;
  border: 1px solid #eceef3;
  border-radius: 12px;
  text-align: left;
}
.fact {
  min-width: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #eceef3;
}
.fact:nth-child(odd) {
  border-right: 1px solid #eceef3;
}
/* No line under the last row: the last fact, and the one beside it when they share it. */
.fact:last-child,
.fact:nth-last-child(2):nth-child(odd) {
  border-bottom: 0;
}
/* An odd one out takes the whole last row. */
.fact:last-child:nth-child(odd) {
  grid-column: 1 / -1;
  border-right: 0;
}
.fact dt {
  font: 400 11.5px "Geist", system-ui, sans-serif;
  color: #8a91a3;
}
.fact dd {
  margin: 2px 0 0;
  font: 600 14px "Geist", system-ui, sans-serif;
  color: #0e1424;
  overflow-wrap: anywhere;
}
.fact-mono dd {
  font: 500 14px "Geist Mono", ui-monospace, monospace;
}
.fact-status dd {
  display: flex;
  align-items: center;
  gap: 6px;
}
.fact-status dd::before {
  content: "";
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a36a;
}
.fact-status.is-progress dd::before {
  background: #e6a23c;
}
</style>
